import {
  type Routine,
  type RoutineLibraryItem,
  useStores,
} from '@littlebird/sdk';
import { ROUTINE_REMOVE_TEAM_TEMPLATE_CONFIRMATION } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button, Text } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Switch } from '@/components/ui/Switch';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { getDeepLinkProtocol } from '@/utils/platform';

const useRoutineTeamSharing = () => {
  const { routines } = useStores();

  const shareRoutine = async (routine: Routine) => {
    const isUpdate = Boolean(routine.teamShare);
    try {
      await routines.shareRoutineWithTeam(routine.referenceId);
      toast.success(
        isUpdate ? 'Team template updated' : 'Shared with your team'
      );
    } catch {
      toast.error('Could not share this routine with your team');
    }
  };

  const removeTeamTemplate = async (teamTemplateId: string) => {
    try {
      await routines.removeTeamRoutineTemplate(teamTemplateId);
      toast.success('Removed from the team library');
    } catch {
      toast.error('Could not remove this team template');
    }
  };

  const confirmRemoveTeamTemplate = async (teamTemplateId: string) => {
    const confirmed = await ModalsManager.showResolvable(
      ConfirmDialog,
      ROUTINE_REMOVE_TEAM_TEMPLATE_CONFIRMATION
    );
    if (confirmed) await removeTeamTemplate(teamTemplateId);
  };

  const copyTemplateLink = async (templateId: string) => {
    const link = `${getDeepLinkProtocol()}://open/routine-chats?template=${encodeURIComponent(templateId)}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  return {
    isPending: (id: number | string) =>
      routines.isOperationPending('share-team', id) ||
      routines.isOperationPending('remove-team-template', id),
    shareRoutine,
    removeTeamTemplate,
    confirmRemoveTeamTemplate,
    copyTemplateLink,
  };
};

export const LibraryCardMenu = observer(
  ({ item }: { item: RoutineLibraryItem }) => {
    const { confirmRemoveTeamTemplate, isPending, copyTemplateLink } =
      useRoutineTeamSharing();

    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label={`Options for ${item.title}`}
            className="absolute right-3 top-3 rounded p-1 text-foreground-muted hover:bg-surface-active hover:text-foreground"
          >
            <Icons.more className="size-4" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" className="min-w-0 w-44">
          <DropdownMenu.Item onSelect={() => copyTemplateLink(item.id)}>
            Copy link
          </DropdownMenu.Item>
          {item.team_template_id && (
            <DropdownMenu.Item
              disabled={isPending(item.team_template_id!)}
              onSelect={() =>
                void confirmRemoveTeamTemplate(item.team_template_id!)
              }
            >
              Remove from team
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
);

const TeamShareFields = observer(({ routine }: { routine: Routine }) => {
  const { isPending, shareRoutine, removeTeamTemplate, copyTemplateLink } =
    useRoutineTeamSharing();

  const share = routine.teamShare;

  const onToggle = (checked: boolean) => {
    if (checked) {
      void shareRoutine(routine);
    } else if (share) {
      void removeTeamTemplate(share.id);
    }
  };

  return (
    <YStack className="gap-2">
      <XStack className="items-start justify-between gap-3">
        <YStack className="min-w-0 gap-0.5">
          <Text className="text-sm font-medium">Share with your team</Text>
          <Text className="text-xs leading-5 text-foreground-muted">
            Includes the name, instructions and schedule, but not runs
          </Text>
        </YStack>
        <Switch
          aria-label="Share with your team"
          checked={Boolean(share)}
          disabled={isPending(share?.id ?? routine.referenceId)}
          onCheckedChange={onToggle}
        />
      </XStack>

      {share && (
        <Button
          variant="outline"
          size="sm"
          className="w-fit gap-1.5"
          onClick={() => copyTemplateLink(share.id)}
        >
          <Icons.link className="size-3.5" />
          Copy link
        </Button>
      )}
    </YStack>
  );
});

export const RoutineTeamShareMenuItems = observer(
  ({ routine }: { routine: Routine }) => {
    const {
      isPending,
      shareRoutine,
      confirmRemoveTeamTemplate,
      copyTemplateLink,
    } = useRoutineTeamSharing();

    if (!subscriptionStore.isTeamMember) return null;

    const share = routine.teamShare;

    if (!share)
      return (
        <DropdownMenu.Item
          disabled={isPending(routine.referenceId)}
          onSelect={() => void shareRoutine(routine)}
        >
          <Icons.share />
          Share with team
        </DropdownMenu.Item>
      );

    return (
      <>
        <DropdownMenu.Item onSelect={() => copyTemplateLink(share.id)}>
          <Icons.link />
          Copy team link
        </DropdownMenu.Item>
        <DropdownMenu.Item
          disabled={isPending(share.id)}
          onSelect={() => void confirmRemoveTeamTemplate(share.id)}
        >
          <Icons.ban />
          Remove from team
        </DropdownMenu.Item>
      </>
    );
  }
);

export const RoutineTeamShareRow = observer(
  ({ routine }: { routine: Routine }) => {
    if (!subscriptionStore.isTeamMember) return null;

    return <TeamShareFields routine={routine} />;
  }
);
